/** Routes. Every page renders inside the Shell (sidebar + search). */
import { createBrowserRouter, type RouteObject } from 'react-router'
import { Shell } from './components/Shell'
import { Home } from './views/Home'
import { BooksBrowse, NamedBrowse, SeriesBrowse, SystemsBrowse } from './views/Browse'
import { SeriesPage } from './views/CollectionPages'
import { Moderation } from './views/Moderation'
import { SystemPage } from './views/SystemPage'
import { BookPage } from './views/BookPage'
import { CreatorPage, PublisherPage } from './views/PeoplePages'
import { History } from './views/History'
import { SearchPage } from './views/Search'
import { RecentChanges, ReviewQueue } from './views/Review'
import { EditPage } from './views/EditPage'
import { About, NewRecord, NotFound } from './views/Misc'
import { Privacy, Terms } from './views/Legal'
import { Loading } from './components/Bits'
import { reloadOnStaleChunk } from './lib/chunks'
import { lazy, Suspense } from 'react'

// Editing and account screens load on demand: most visitors only browse.
const forms = reloadOnStaleChunk(() => import('./forms/RecordForms'))
const account = reloadOnStaleChunk(() => import('./views/Account'))
const developers = reloadOnStaleChunk(() => import('./views/Developers'))
const support = reloadOnStaleChunk(() => import('./views/Support'))
const SystemForm = lazy(() => forms().then((m) => ({ default: m.SystemForm })))
const BookForm = lazy(() => forms().then((m) => ({ default: m.BookForm })))
const NamedForm = lazy(() => forms().then((m) => ({ default: m.NamedForm })))
const SeriesForm = lazy(() => forms().then((m) => ({ default: m.SeriesForm })))
const SignIn = lazy(() => account().then((m) => ({ default: m.SignIn })))
const Account = lazy(() => account().then((m) => ({ default: m.Account })))
const UserPage = lazy(() => account().then((m) => ({ default: m.UserPage })))
const Admin = lazy(() => account().then((m) => ({ default: m.Admin })))
const Developers = lazy(() => developers().then((m) => ({ default: m.Developers })))
const Support = lazy(() => support().then((m) => ({ default: m.Support })))

const later = (el: React.ReactNode) => <Suspense fallback={<Loading />}>{el}</Suspense>

export const routes: RouteObject[] = [
  {
    element: <Shell />,
    children: [
      { index: true, element: <Home /> },
      { path: 'systems', element: <SystemsBrowse /> },
      { path: 'systems/new', element: later(<SystemForm />) },
      { path: 'systems/:id', element: <SystemPage /> },
      { path: 'systems/:id/edit', element: later(<SystemForm />) },
      { path: 'books', element: <BooksBrowse /> },
      { path: 'books/new', element: later(<BookForm />) },
      { path: 'books/:id', element: <BookPage /> },
      { path: 'books/:id/edit', element: later(<BookForm />) },
      { path: 'series', element: <SeriesBrowse /> },
      { path: 'series/new', element: later(<SeriesForm />) },
      { path: 'series/:id', element: <SeriesPage /> },
      { path: 'series/:id/edit', element: later(<SeriesForm />) },
      { path: 'publishers', element: <NamedBrowse type="publisher" /> },
      { path: 'publishers/new', element: later(<NamedForm type="publisher" />) },
      { path: 'publishers/:id', element: <PublisherPage /> },
      { path: 'publishers/:id/edit', element: later(<NamedForm type="publisher" />) },
      { path: 'creators', element: <NamedBrowse type="creator" /> },
      { path: 'creators/new', element: later(<NamedForm type="creator" />) },
      { path: 'creators/:id', element: <CreatorPage /> },
      { path: 'creators/:id/edit', element: later(<NamedForm type="creator" />) },
      { path: ':kind/:id/history', element: <History /> },
      { path: 'search', element: <SearchPage /> },
      { path: 'review', element: <ReviewQueue /> },
      { path: 'changes', element: <RecentChanges /> },
      { path: 'edits/:id', element: <EditPage /> },
      { path: 'new', element: <NewRecord /> },
      { path: 'signin', element: later(<SignIn />) },
      { path: 'account', element: later(<Account />) },
      { path: 'users/:id', element: later(<UserPage />) },
      { path: 'admin', element: later(<Admin />) },
      { path: 'about', element: <About /> },
      { path: 'privacy', element: <Privacy /> },
      { path: 'terms', element: <Terms /> },
      { path: 'developers', element: later(<Developers />) },
      { path: 'support', element: later(<Support />) },
      { path: 'moderation', element: <Moderation /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]

export const createRouter = () => createBrowserRouter(routes)
